import { z } from "zod"
import { Address } from "viem"
import { ProjectConfigSchema, type ProjectConfigType } from "@tutellus/dappcomposer-contracts-ifaces"
import { ProjectConfigToken } from "./ProjectConfigToken"
import { ProjectConfigPool } from "./ProjectConfigPool"
import { ProjectConfigSynthetic } from "./ProjectConfigSynthetic"
import { ProjectConfigGauge } from "./ProjectConfigGauge"
import { ProjectConfigBribe } from "./ProjectConfigBribe"
import { ProjectConfigDexRouter } from "./ProjectConfigDexRouter"
import { ProjectConfigAddress } from "./ProjectConfigAddress"

const ProjectConfigValidationSchema = z.object({
  id: z.string({ required_error: "id is required" }),
  key: z.string({ required_error: "key is required" }),
  chainID: z.number({ required_error: "chainID is required" }),
  addresses: z.array(z.instanceof(ProjectConfigAddress)),
  tokens: z.array(z.instanceof(ProjectConfigToken)),
  pools: z.array(z.instanceof(ProjectConfigPool)),
  balances: z.array(z.string()),
  dexRouters: z.array(z.instanceof(ProjectConfigDexRouter)),
  synthetics: z.array(z.instanceof(ProjectConfigSynthetic)),
  gauges: z.array(z.instanceof(ProjectConfigGauge)),
  bribes: z.array(z.instanceof(ProjectConfigBribe)),
})

export class ProjectConfig {
  constructor(
    private readonly _id: string,
    private readonly _key: string,
    private readonly _chainID: number,
    private readonly _addresses: ProjectConfigAddress[],
    private readonly _tokens: ProjectConfigToken[],
    private readonly _pools: ProjectConfigPool[],
    private readonly _balances: string[],
    private readonly _dexRouters: ProjectConfigDexRouter[],
    private readonly _synthetics: ProjectConfigSynthetic[],
    private readonly _gauges: ProjectConfigGauge[],
    private readonly _bribes: ProjectConfigBribe[],
  ) {}

  static create({
    id,
    key,
    chainID,
    addresses,
    tokens,
    pools,
    balances,
    dexRouters,
    synthetics,
    gauges,
    bribes,
  }: z.infer<typeof ProjectConfigValidationSchema>) {
    const { success, error } = ProjectConfigValidationSchema.safeParse({
      id,
      key,
      chainID,
      addresses,
      tokens,
      pools,
      balances,
      dexRouters,
      synthetics,
      gauges,
      bribes,
    })
    if (!success) throw new Error(error.message)

    return new ProjectConfig(
      id,
      key,
      chainID,
      addresses,
      tokens,
      pools,
      balances,
      dexRouters,
      synthetics,
      gauges,
      bribes,
    )
  }

  static createFromAPI({
    id,
    key,
    chainID,
    tokens,
    pools,
    addresses,
    externalSources,
    balances,
    dexRouters = [],
    synthetics = [],
    gauges = [],
    bribes = [],
  }: ProjectConfigType) {
    const { success, error } = ProjectConfigSchema.safeParse({
      id,
      key,
      chainID,
      tokens,
      pools,
      addresses,
      externalSources,
      balances,
      dexRouters,
      synthetics,
      gauges,
      bribes,
    })

    if (!success) throw new Error(error.message)
    if (!dexRouters?.length) throw new Error("Invalid dexRouters project config")
    if (!synthetics?.length) throw new Error("Invalid synthetics project config")
    if (!gauges?.length) throw new Error("Invalid gauges project config")
    if (!bribes?.length) throw new Error("Invalid bribes project config")

    return new ProjectConfig(
      id,
      key,
      chainID,
      addresses.map((address) =>
        ProjectConfigAddress.create({
          tokenID: address.tokenID,
          address: address.address as Address,
          feed: address.feed,
        }),
      ),
      tokens.map((token) => ProjectConfigToken.createFromAPI(token)),
      pools.map((pool) => ProjectConfigPool.createFromAPI(pool)),
      balances,
      dexRouters.map((router) => ProjectConfigDexRouter.createFromAPI(router)),
      synthetics.map((synthetic) => ProjectConfigSynthetic.createFromAPI(synthetic)),
      gauges.map((gauge) => ProjectConfigGauge.createFromAPI(gauge)),
      bribes.map((bribe) => ProjectConfigBribe.createFromAPI(bribe)),
    )
  }

  get id() {return this._id} // prettier-ignore
  get key() {return this._key} // prettier-ignore
  get chainID() {return this._chainID} // prettier-ignore
  get tokens() {return this._tokens} // prettier-ignore
  get pools() {return this._pools} // prettier-ignore
  get addresses() {return this._addresses} // prettier-ignore
  get balances() {return this._balances} // prettier-ignore
  get dexRouters() {return this._dexRouters} // prettier-ignore
  get synthetics() {return this._synthetics} // prettier-ignore
  get gauges () { return this._gauges } // prettier-ignore
  get bribes () { return this._bribes } // prettier-ignore

  getTokenConfig(tokenID: string) {
    const tokenConfig = this.tokens.find((token) => token.id === tokenID)
    if (!tokenConfig) throw new Error("tokenID is required")
    return tokenConfig
  }

  getSyntheticConfig(syntheticID: string) {
    const syntheticConfig = this.synthetics.find((synthetic) => synthetic.id === syntheticID)
    if (!syntheticConfig) throw new Error("syntheticID is required")
    return syntheticConfig
  }

  getAddressByTokenID(tokenID: string) {
    const tokenAddress = this.addresses.find((tokenData) => tokenData.tokenID === tokenID)
    if (!tokenAddress) throw new Error(`Token address not found: ${tokenID}`)
    return tokenAddress.address
  }

  getBribeAddressByPoolID(poolID: string) {
    const bribeAddress = this.bribes.find((bribe) => bribe.poolID === poolID)?.address
    if (!bribeAddress) throw new Error(`Bribe address not found: ${poolID}`)
    return bribeAddress
  }

  serialize() {
    return {
      id: this.id,
      key: this.key,
      chainID: this.chainID,
      addresses: this.addresses,
      tokens: this.tokens.map((token) => token.serialize()),
      pools: this.pools.map((pool) => pool.serialize()),
      balances: this.balances,
      dexRouters: this.dexRouters,
      synthetics: this.synthetics.map((synthetic) => synthetic.serialize()),
      gauges: this.gauges.map((gauge) => gauge.serialize()),
      bribes: this.bribes.map((bribe) => bribe.serialize()),
    }
  }
}

export type ProjectConfigSerialized = ReturnType<ProjectConfig["serialize"]>
